'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'sweetspot_storefront_cart_v1';

export interface StorefrontCartItem {
  slug: string;
  vendorName: string;
  vendorAvatarUrl?: string;
  productId: string;
  title: string;
  unit: string;
  quantity: number;
  minQty: number;
  price: number;
  imageUrl?: string;
}

interface StorefrontCartContextValue {
  items: StorefrontCartItem[];
  addItem: (item: Omit<StorefrontCartItem, 'quantity'> & { quantity?: number }) => void;
  updateQuantity: (slug: string, productId: string, quantity: number) => void;
  removeItem: (slug: string, productId: string) => void;
  clearStore: (slug: string) => void;
  clearAll: () => void;
}

const StorefrontCartContext = createContext<StorefrontCartContextValue | null>(null);

function sanitizeItems(value: unknown): StorefrontCartItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item === 'object')
    .map((item) => item as Partial<StorefrontCartItem>)
    .filter((item) => item.slug && item.productId && item.title)
    .map((item) => ({
      slug: String(item.slug),
      vendorName: String(item.vendorName ?? item.slug ?? 'Store'),
      vendorAvatarUrl: item.vendorAvatarUrl ? String(item.vendorAvatarUrl) : undefined,
      productId: String(item.productId),
      title: String(item.title),
      unit: String(item.unit ?? 'each'),
      quantity: Math.max(1, Number(item.quantity ?? 1)),
      minQty: Math.max(1, Number(item.minQty ?? 1)),
      price: Number.isFinite(Number(item.price)) ? Number(item.price) : 0,
      imageUrl: item.imageUrl ? String(item.imageUrl) : undefined,
    }));
}

export function StorefrontCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<StorefrontCartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setItems(sanitizeItems(parsed));
    } catch {
      setItems([]);
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const addItem = useCallback(
    (incoming: Omit<StorefrontCartItem, 'quantity'> & { quantity?: number }) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('analytics:add_to_cart', {
            detail: {
              slug: incoming.slug,
              productId: incoming.productId,
              quantity: Number(incoming.quantity ?? incoming.minQty),
              price: incoming.price,
            },
          }),
        );
      }

      setItems((current) => {
        const nextQty = Math.max(incoming.minQty, Number(incoming.quantity ?? incoming.minQty));
        const index = current.findIndex(
          (item) => item.slug === incoming.slug && item.productId === incoming.productId,
        );
        if (index === -1) {
          return [...current, { ...incoming, quantity: nextQty }];
        }

        const next = [...current];
        const prev = next[index];
        next[index] = {
          ...prev,
          vendorName: incoming.vendorName,
          vendorAvatarUrl: incoming.vendorAvatarUrl,
          title: incoming.title,
          price: incoming.price,
          unit: incoming.unit,
          imageUrl: incoming.imageUrl,
          minQty: incoming.minQty,
          quantity: Math.max(incoming.minQty, prev.quantity + nextQty),
        };
        return next;
      });
    },
    [],
  );

  const updateQuantity = useCallback((slug: string, productId: string, quantity: number) => {
    setItems((current) => {
      return current.map((item) => {
        if (item.slug !== slug || item.productId !== productId) return item;
        return {
          ...item,
          quantity: Math.max(item.minQty, Math.floor(quantity)),
        };
      });
    });
  }, []);

  const removeItem = useCallback((slug: string, productId: string) => {
    setItems((current) =>
      current.filter((item) => !(item.slug === slug && item.productId === productId)),
    );
  }, []);

  const clearStore = useCallback((slug: string) => {
    setItems((current) => current.filter((item) => item.slug !== slug));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
  }, []);

  const value = useMemo<StorefrontCartContextValue>(
    () => ({ items, addItem, updateQuantity, removeItem, clearStore, clearAll }),
    [items, addItem, updateQuantity, removeItem, clearStore, clearAll],
  );

  return (
    <StorefrontCartContext.Provider value={value}>
      {children}
    </StorefrontCartContext.Provider>
  );
}

export function useStorefrontCart() {
  const context = useContext(StorefrontCartContext);
  if (!context) {
    throw new Error('useStorefrontCart must be used within StorefrontCartProvider');
  }
  return context;
}
